import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, renderSlot as _renderSlot, resolveComponent as _resolveComponent, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-3a0b5d95"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "ident"
}
const _hoisted_2 = {
  key: 1,
  class: "dot"
}
const _hoisted_3 = { class: "itemText" }
const _hoisted_4 = ["title"]
const _hoisted_5 = { class: "nameAndStatus" }
const _hoisted_6 = { class: "name" }
const _hoisted_7 = {
  key: 0,
  class: "line"
}
const _hoisted_8 = ["textContent"]
const _hoisted_9 = ["textContent"]

import { computed, ComputedRef, useSlots } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import NotificationDot from '@/components/NotificationDot.vue';
import TextWithHighlightedTerm from '@/components/search/TextWithHighlightedTerm.vue';
import UserIdent from '@/components/UserIdent.vue';
import { TextWithHighlight } from '@/data/datatypes/TextWithHighlight';
import { Track } from '@/data/datatypes/Track';
import { LimitedUserDetails } from '@/data/datatypes/UserDetails';
import { getChatName } from '@/data/helpers/ChatHelper';
import { getRecentActivityDate } from '@/data/helpers/DateHelper';
import { formatNotificationCount } from '@/data/helpers/TrackHelper';
import { useUserStore } from '@/stores/User';

interface Props {
  to: RouteLocationRaw;
  name?: string | null;
  subtext?: string | null;
  // e.g. user status for direct chat
  online?: boolean | null;
  // e.g. new message
  numNotifications?: number | null;
  displayDate?: number | null;
  date?: Date | null;
  // This will override the default selection highlighting based on the route specified in the 'to' prop
  selected?: boolean | null;
  track?: Track | null;
  user?: LimitedUserDetails | null;
  hideIdent?: boolean;
  highlightRegex?: RegExp;
  textWithHighlights?: TextWithHighlight[];
}

interface Emits {
  (e: 'click', event: MouseEvent): void,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'NavListItem',
  props: {
    to: {},
    name: { default: null },
    subtext: { default: null },
    online: { type: [Boolean, null], default: null },
    numNotifications: { default: null },
    displayDate: { default: null },
    date: { default: null },
    selected: { type: [Boolean, null], default: null },
    track: { default: null },
    user: { default: null },
    hideIdent: { type: Boolean, default: false },
    highlightRegex: {},
    textWithHighlights: {}
  },
  emits: ["click"],
  setup(__props: any, { emit: __emit }) {

const userStore = useUserStore();

const slots = useSlots();

const props = __props;

const emit = __emit;

const currentUserId: ComputedRef<string> = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userStore.currentUserId!;
});

const showIdent: ComputedRef<boolean> = computed(() => {
  return !props.hideIdent && (isChat.value || isUser.value || !!props.name);
});

const linkSelected: ComputedRef<string | null> = computed(() => {
  // If 'selected' hasn't been specified, then let it default to the route matching for the 'to' prop
  if (props.selected === undefined || props.selected === null) {
    return null;
  }

  return props.selected ? 'selected' : 'notSelected';
});

const isChat: ComputedRef<boolean> = computed(() => {
  if (props.track) {
    return isPrivateChat(props.track);
  }
  return false;
});

const isUser: ComputedRef<boolean> = computed(() => {
  return !!props.user;
});

const isGroupChat: ComputedRef<boolean> = computed(() => {
  let groupChat: boolean = false;
  if (isChat.value && props.track?.typeMetadata) {
    const commaIndex = props.track.typeMetadata.indexOf(',');
    groupChat = !!(commaIndex && commaIndex !== props.track.typeMetadata.lastIndexOf(','));
  }
  return groupChat;
});

const isNotificationsSlotPopulated: ComputedRef<boolean> = computed(() => {
  return !!slots.notification;
});

const formattedName: ComputedRef<string> = computed(() => {
  if (props.name) {
    return props.name;
  }
  if (props.track) {
    return getChatName(props.track, currentUserId.value, userStore.displayDetailsForUsers);
  }
  return '';
});

const formattedDate: ComputedRef<string | null> = computed(() => {
  const parsedDate: Date | null = props.displayDate ? new Date(props.displayDate) : props.date;
  if (!parsedDate) {
    return null;
  }

  return getRecentActivityDate(parsedDate);
});

const userIdent: ComputedRef<LimitedUserDetails | null> = computed(() => {
  if (props.user) {
    return props.user;
  }
  if (!isChat.value) {
    return null;
  }
  if (privateChatUsers.value.length) {
    return privateChatUsers.value[0];
  }
  return null;
});

const privateChatUsers: ComputedRef<LimitedUserDetails[]> = computed(() => {
  if (props.track) {
    return getPrivateChatUsers(props.track, currentUserId.value, userStore.displayDetailsForUsers);
  }
  return [];
});

const displayNumber: ComputedRef<number | undefined> = computed(() => {
  if (isGroupChat.value) {
    return privateChatUsers.value.length;
  }
  return undefined;
});

function handleClick(event: MouseEvent): MouseEvent {
  emit('click', event);
  return event;
}

function formatNumMessages(num: number): string | null {
  return formatNotificationCount(num);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(_component_router_link, {
    class: _normalizeClass(["navListItem", [linkSelected.value, { withIdent: showIdent.value }]]),
    to: _ctx.to,
    onClick: handleClick
  }, {
    default: _withCtx(() => [
      (showIdent.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(UserIdent, {
              class: "avatar",
              user: userIdent.value,
              altDisplayName: formattedName.value,
              title: isGroupChat.value ? formattedName.value : undefined,
              displayNumber: displayNumber.value
            }, null, 8, ["user", "altDisplayName", "title", "displayNumber"])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_2)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          class: _normalizeClass(["line", {hasNotification: !!_ctx.numNotifications}]),
          title: formattedName.value
        }, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(TextWithHighlightedTerm, {
                text: formattedName.value,
                highlightRegex: _ctx.highlightRegex,
                textWithHighlights: _ctx.textWithHighlights
              }, null, 8, ["text", "highlightRegex", "textWithHighlights"])
            ])
          ]),
          (!isNotificationsSlotPopulated.value && !!_ctx.numNotifications)
            ? (_openBlock(), _createBlock(NotificationDot, {
                key: 0,
                count: formatNumMessages(_ctx.numNotifications)
              }, null, 8, ["count"]))
            : _createCommentVNode("", true)
        ], 10, _hoisted_4),
        (_ctx.subtext)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_ctx.subtext)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "subtext",
                    textContent: _toDisplayString(_ctx.subtext)
                  }, null, 8, _hoisted_8))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      (formattedDate.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: "date",
            textContent: _toDisplayString(formattedDate.value)
          }, null, 8, _hoisted_9))
        : _createCommentVNode("", true),
      _renderSlot(_ctx.$slots, "notification")
    ], undefined, true),
    _: 3
  }, 8, ["class", "to"]))
}
}

})